import React, { memo } from 'react';
import { type Suspect } from '../api/types.js';

const NODE_POSITIONS = {
  0: { x: 72, y: 72, r: 19 },
  1: { x: 212, y: 58, r: 16 },
  2: { x: 255, y: 125, r: 14 },
  3: { x: 140, y: 100, r: 12 },
  4: { x: 188, y: 155, r: 10 },
  5: { x: 36, y: 138, r: 9 },
  6: { x: 100, y: 40, r: 9 },
  7: { x: 255, y: 60, r: 14 }, // Added when injected, connected to A-033
};

const LEVEL_COLORS = {
  h: '#e63946',
  m: '#f4a261',
  l: '#4cc9f0',
};

interface MapFiltersProps {
  suspects: Record<string, Suspect>;
  onSelectSuspect: (id: string) => void;
}

const MiniSocialGraph = memo(function MiniSocialGraph({ suspects, onSelectSuspect }: MapFiltersProps) {
  const suspectIds = Object.keys(suspects);

  const edges = new Set<string>();
  const nodePositions = {};
  suspectIds
    .sort((a, b) => a.localeCompare(b))
    .forEach((fromId) => {
      const connections = suspects[fromId].connections;
      connections.forEach((toId) => {
        if (!toId) return;
        const connection = `${fromId}__${toId}`;
        if (!edges.has(connection) && !edges.has(`${toId}__${fromId}`)) {
          // TODO: remove later
          if (!nodePositions[fromId]) {
            const idx = Object.keys(nodePositions).length;
            if (idx <= 7) {
              nodePositions[fromId] = NODE_POSITIONS[idx];
            }
          }
          // TODO: remove later
          if (!nodePositions[toId]) {
            const idx = Object.keys(nodePositions).length;
            if (idx <= 7) {
              nodePositions[toId] = NODE_POSITIONS[idx];
            }
          }

          edges.add(connection);
        }
      });
    });

  return (
    <div className="graph-canvas">
      <svg viewBox="0 0 285 180" preserveAspectRatio="xMidYMid meet">
        <defs>
          {suspectIds.map((id) => {
            const s = suspects[id];
            const color = LEVEL_COLORS[s.level];
            return (
              <radialGradient key={`glow-${id}`} id={`glow-${id}`}>
                <stop offset="0%" stopColor={color} stopOpacity="0.3" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </radialGradient>
            );
          })}
        </defs>

        {/* Edges */}
        {Array.from(edges)
          .map((e) => e.split('__'))
          .map(([from, to]) => {
            if (!suspects[from] || !suspects[to]) return null;
            const p1 = nodePositions[from];
            const p2 = nodePositions[to];
            if (!p1 || !p2) return null;
            return (
              <line
                key={`${from}-${to}`}
                x1={p1.x}
                y1={p1.y}
                x2={p2.x}
                y2={p2.y}
                stroke="rgba(212,175,55,.22)"
                strokeWidth="1.5"
                strokeDasharray="4 3"
              />
            );
          })}

        {/* Glow halos */}
        {suspectIds.map((id) => {
          const pos = nodePositions[id];
          if (!pos) return null;
          return <circle key={`halo-${id}`} cx={pos.x} cy={pos.y} r={pos.r + 8} fill={`url(#glow-${id})`} />;
        })}

        {/* Nodes */}
        {suspectIds.map((id) => {
          const s = suspects[id];
          const pos = nodePositions[id];
          if (!pos) return null;
          const color = LEVEL_COLORS[s.level];
          return (
            <g key={id} style={{ cursor: 'pointer' }} onClick={() => onSelectSuspect(id)}>
              <circle cx={pos.x} cy={pos.y} r={pos.r} fill={color} stroke="#d4af37" strokeWidth="1.5" opacity="0.9" />
              <text
                x={pos.x}
                y={pos.y + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#fff"
                fontSize="7"
                fontFamily="'JetBrains Mono', monospace"
                fontWeight="600"
              >
                ...{id.slice(-6)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
});

export default MiniSocialGraph;
